// @flow
'use strict';

import React, { PureComponent } from 'react';

import {
  Animated,
  Dimensions,
  Easing,
  Image,
  PanResponder,
  StyleSheet,
  View,
} from 'react-native';

import PropTypes from 'prop-types';
import { getSeverityColor } from '../utilities/colorutils';
import { Text } from '../../design/components';
import Spacer from '../../design/components/Spacer';
import LinearGradient from 'react-native-web-linear-gradient';
import { withTheme } from '../../design/styles/useThemeContext';

var TRACK_SIZE = 4;
var THUMB_SIZE = 30;

function Rect(x, y, width, height) {
  this.x = x;
  this.y = y;
  this.width = width;
  this.height = height;
}

Rect.prototype.containsPoint = function (x, y) {
  return (
    x >= this.x &&
    y >= this.y &&
    x <= this.x + this.width &&
    y <= this.y + this.height
  );
};

var DEFAULT_ANIMATION_CONFIGS = {
  spring: {
    friction: 7,
    tension: 100,
  },
  timing: {
    duration: 150,
    easing: Easing.inOut(Easing.ease),
    delay: 0,
  },
  // decay : { // This has a serious bug
  //   velocity     : 1,
  //   deceleration : 0.997
  // }
};

class Slider extends PureComponent {
  static propTypes = {
    /**
     * Initial value of the slider. The value should be between minimumValue
     * and maximumValue, which default to 0 and 1 respectively.
     * Default value is 0.
     *
     * *This is not a controlled component*, e.g. if you don't update
     * the value, the component won't be reset to its inital value.
     */
    value: PropTypes.number,

    /**
     * If true the user won't be able to move the slider.
     * Default value is false.
     */
    disabled: PropTypes.bool,

    /**
     * Initial minimum value of the slider. Default value is 0.
     */
    minimumValue: PropTypes.number,

    /**
     * Initial maximum value of the slider. Default value is 1.
     */
    maximumValue: PropTypes.number,

    /**
     * Step value of the slider. The value should be between 0 and
     * (maximumValue - minimumValue). Default value is 0.
     */
    step: PropTypes.number,

    /**
     * The color used for the track to the left of the button. Overrides the
     * default blue gradient image.
     */
    minimumTrackTintColor: PropTypes.string,

    /**
     * The color used for the track to the right of the button. Overrides the
     * default blue gradient image.
     */
    maximumTrackTintColor: PropTypes.string,

    /**
     * The color used for the thumb.
     */
    thumbTintColor: PropTypes.string,

    /**
     * The size of the touch area that allows moving the thumb.
     * The touch area has the same center has the visible thumb.
     * This allows to have a visually small thumb while still allowing the user
     * to move it easily.
     * The default is {width: 40, height: 40}.
     */
    thumbTouchSize: PropTypes.shape({
      width: PropTypes.number,
      height: PropTypes.number,
    }),

    /**
     * Callback continuously called while the user is dragging the slider.
     */
    onValueChange: PropTypes.func,

    /**
     * Callback called when the user starts changing the value (e.g. when
     * the slider is pressed).
     */
    onSlidingStart: PropTypes.func,

    /**
     * Callback called when the user finishes changing the value (e.g. when
     * the slider is released).
     */
    onSlidingComplete: PropTypes.func,

    /**
     * The style applied to the slider container.
     */
    // style: ViewPropTypes.style,

    /**
     * The style applied to the track.
     */
    // trackStyle: ViewPropTypes.style,

    /**
     * The style applied to the thumb.
     */
    // thumbStyle: ViewPropTypes.style,

    /**
     * Sets an image for the thumb.
     */
    thumbImage: PropTypes.any,

    /**
     * Set this to true to visually see the thumb touch rect in green.
     */
    debugTouchArea: PropTypes.bool,

    /**
     * Set to true to animate values with default 'timing' animation type
     */
    animateTransitions: PropTypes.bool,

    /**
     * Custom Animation type. 'spring' or 'timing'.
     */
    animationType: PropTypes.oneOf(['spring', 'timing']),

    /**
     * Used to configure the animation parameters.  These are the same parameters in the Animated library.
     */
    animationConfig: PropTypes.object,

    /**
     * Set to true to update the value whilst clicking the Slider
     */
    trackClickable: PropTypes.bool,

    /**
     * Custom props, defining slider behaviour on first renders
     * In first renders (two times) - thumb is placed on zero because of
     * containerSize.width == 0 in inner state - this custom props overrides
     * such behaviour
     * Since there is not access to native element, which is used in onLayout
     * we can use this way
     * ThumbSize is possibly duplicating, but there should be only width and height
     * useInitialContainerSize - forces slider to not to change initial container sizes, passed via  custom props
     */
    containerSize: PropTypes.shape({
      width: PropTypes.number,
      height: PropTypes.number,
    }),

    trackSize: PropTypes.shape({
      width: PropTypes.number,
      height: PropTypes.number,
    }),

    thumbSize: PropTypes.shape({
      width: PropTypes.number,
      height: PropTypes.number,
    }),

    useInitialContainerSize: PropTypes.bool,

    betweenMode: PropTypes.bool,
  };

  static defaultProps = {
    value: 0,
    minimumValue: 0,
    maximumValue: 1,
    step: 0,
    minimumTrackTintColor: '#3f3f3f',
    maximumTrackTintColor: '#b3b3b3',
    thumbTintColor: '#343434',
    thumbTouchSize: { width: 40, height: 40 },
    debugTouchArea: false,
    animationType: 'timing',
    trackClickable: false,

    containerSize: { width: Dimensions.get('window').width, height: 0 },
    trackSize: { width: Dimensions.get('window').width - 60, height: 0 },
    thumbSize: { width: 30, height: 0 },
  };

  state = {
    containerSize: this.props.containerSize, // {width: 0, height: 0}
    trackSize: this.props.trackSize, // {width: 0, height: 0}
    thumbSize: this.props.thumbSize, // {width: 0, height: 0}
    allMeasured:
      this.props.containerSize && this.props.trackSize && this.props.thumbSize, // false
    value: new Animated.Value(+this.props.value || 0),
    valueEnd: new Animated.Value(+this.props.valueEnd || 10),
  };

  UNSAFE_componentWillMount() {
    this._panResponder = PanResponder.create({
      onStartShouldSetPanResponder: this._handleStartShouldSetPanResponder,
      onMoveShouldSetPanResponder: this._handleMoveShouldSetPanResponder,
      onPanResponderGrant: this._handlePanResponderGrant,
      onPanResponderMove: this._handlePanResponderMove,
      onPanResponderRelease: this._handlePanResponderEnd,
      onPanResponderTerminationRequest: this._handlePanResponderRequestEnd,
      onPanResponderTerminate: this._handlePanResponderEnd,
    });
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    var newValue = +nextProps.value || 0;

    if (this.props.value !== newValue) {
      if (this.props.animateTransitions) {
        this._setCurrentValueAnimated(newValue);
      } else {
        this._setCurrentValue(newValue);
      }
    }
    if (this.props.valueEnd !== nextProps.valueEnd) {
      this._setEndValue(+nextProps.valueEnd);
    }
  }

  renderLines = () => {
    const { colors } = this.props.theme;
    let result = [];
    for (let i = 1; i <= 10; i++) {
      result.push(
        <View
          key={'key_' + i}
          style={{
            backgroundColor: colors.black,
            opacity: 0.05,
            width: 1,
            height: 14,
            position: 'absolute',
            top: 13,
            bottom: 0,
            left: (this.state.trackSize.width / 11) * i,
          }}
        />,
      );
    }
    return result;
  };

  renderBetweenLabels = () => {
    return (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        <View style={{ alignItems: 'center', marginBottom: 5 }}>
          <View
            style={{
              backgroundColor: getSeverityColor(
                this.state.value.__getValue(),
                this.props.mode,
              ),
              borderRadius: 5,
              width: 50,
              height: 43,
            }}
          />
          <Text
            style={{
              fontSize: 21,
              color: '#303639',
              position: 'absolute',
              marginTop: 8,
            }}
          >
            {Math.round(this.state.value.__getValue())}
          </Text>
        </View>
        <Spacer />
        <View style={{ alignItems: 'center', marginBottom: 5 }}>
          <View
            style={{
              backgroundColor: getSeverityColor(
                this.state.valueEnd.__getValue(),
                this.props.mode,
              ),
              borderRadius: 5,
              width: 50,
              height: 43,
            }}
          />
          <Text
            style={{
              fontSize: 21,
              color: '#303639',
              position: 'absolute',
              marginTop: 8,
            }}
          >
            {Math.round(this.state.valueEnd.__getValue())}
          </Text>
        </View>
      </View>
    );
  };

  renderLabel = (thumbLeft) => {
    const value = this.state.value.__getValue();
    const progressColor = getSeverityColor(value, this.props.mode);
    return this.props.mode === 'severity' ? (
      <Animated.View
        style={{
          marginLeft: -10,
          marginBottom: 5,
          width: 50,
          transform: [{ translateX: thumbLeft }],
          alignItems: 'center',
        }}
      >
        <View
          style={{
            backgroundColor: progressColor,
            borderRadius: 5,
            width: 50,
            height: 43,
          }}
        />
        <View
          style={{
            height: 21,
            width: 21,
            borderRadius: 3,
            marginTop: -15,
            marginBottom: 5,
            transform: [{ rotateZ: '45deg' }],
            backgroundColor: progressColor,
          }}
        />
        <Text
          style={{
            fontSize: 21,
            color: '#303639',
            position: 'absolute',
            marginTop: 8,
          }}
        >
          {Math.round(value)}
        </Text>
      </Animated.View>
    ) : (
      <Animated.View
        style={{
          marginLeft: -10,
          marginBottom: 5,
          width: 50,
          height: 50,
          transform: [{ translateX: thumbLeft }],
          alignItems: 'center',
        }}
      >
        <Image
          style={{
            tintColor: progressColor,
            height: 50,
            width: 50,
            alignSelf: 'center',
            position: 'absolute',
            resizeMode: 'contain',
          }}
          source={require('../../modules/Records/assets/star.png')}
        />
        <Text
          style={{
            fontSize: 21,
            color: '#303639',
            position: 'absolute',
            marginTop: 12,
          }}
        >
          {Math.round(value)}
        </Text>
      </Animated.View>
    );
  };
  colors = () => {
    return [
      getSeverityColor(this.props.mode === 'severity' ? 0 : 10),
      getSeverityColor(5),
      getSeverityColor(this.props.mode === 'severity' ? 10 : 0),
    ];
  };

  render() {
    var {
      minimumValue,
      maximumValue,
      minimumTrackTintColor,
      maximumTrackTintColor,
      thumbTintColor,
      thumbImage,
      styles,
      style,
      trackStyle,
      thumbStyle,
      debugTouchArea,
      trackClickable,
      ...other
    } = this.props;
    const { colors } = this.props.theme;
    var { value, valueEnd, containerSize, trackSize, thumbSize, allMeasured } =
      this.state;
    var mainStyles = styles || defaultStyles;
    var thumbLeft = value.interpolate({
      inputRange: [minimumValue, maximumValue],
      outputRange: [0, trackSize.width - thumbSize.width],
      //extrapolate: 'clamp',
    });
    var thumbEndLeft = valueEnd.interpolate({
      inputRange: [minimumValue, maximumValue],
      outputRange: [0, trackSize.width - thumbSize.width],
      //extrapolate: 'clamp',
    });
    var valueVisibleStyle = {};
    if (!allMeasured) {
      valueVisibleStyle.opacity = 0;
    }

    var minimumTrackStyle = {
      position: 'absolute',
      width: Animated.add(thumbLeft, thumbSize.width / 2),
      backgroundColor: minimumTrackTintColor,
      ...valueVisibleStyle,
    };

    var touchOverflowStyle = this._getTouchOverflowStyle();

    return (
      <View
        onLayout={this._measureContainer}
        style={{ paddingLeft: 30, paddingRight: 30, overflow: 'hidden' }}
      >
        {this.props.betweenMode
          ? this.renderBetweenLabels()
          : this.renderLabel(thumbLeft)}
        <View {...other} style={[mainStyles.container, style]}>
          <View
            style={{
              backgroundImage: 'linear-gradient(90deg, "#FF0000", "#00FF00")',
              position: 'absolute',
              left: 0,
              right: 0,
              height: 15,
              borderRadius: 5,
              alignSelf: 'stretch',
            }}
          />
          <LinearGradient
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            onLayout={this._measureTrack}
            style={{
              left: 0,
              right: 0,
              borderRadius: 5,
              height: 15,
              position: 'absolute',
              alignSelf: 'stretch',
            }}
            colors={this.colors()}
          />
          {this.props.betweenMode && this.state.allMeasured && (
            <Animated.View
              style={{
                backgroundColor: colors.gray,
                position: 'absolute',
                left: 0,
                right:
                  this.state.trackSize.width -
                  this._getThumbLeft(this.state.value.__getValue()) -
                  this.state.thumbSize.width / 2,
                height: 15,
                borderRadius: 5,
                alignSelf: 'stretch',
              }}
            />
          )}
          {this.props.betweenMode && this.state.allMeasured && (
            <Animated.View
              style={{
                backgroundColor: colors.gray,
                position: 'absolute',
                left:
                  this._getThumbLeft(this.state.valueEnd.__getValue()) +
                  this.state.thumbSize.width / 2,
                right: 0,
                height: 15,
                borderRadius: 5,
                alignSelf: 'stretch',
              }}
            />
          )}
          {this.renderLines(thumbLeft)}
          <Animated.View
            onLayout={this._measureThumb}
            renderToHardwareTextureAndroid={true}
            style={[
              { position: 'absolute' },
              {
                transform: [{ translateX: thumbLeft }],
              },
            ]}
          >
            {this._renderThumbImage()}
          </Animated.View>
          {this.props.betweenMode && (
            <Animated.View
              onLayout={this._measureThumb}
              renderToHardwareTextureAndroid={true}
              style={[
                { position: 'absolute' },
                {
                  transform: [{ translateX: thumbEndLeft }],
                },
              ]}
            >
              {this._renderThumbImage()}
            </Animated.View>
          )}
          <View
            renderToHardwareTextureAndroid={true}
            style={[defaultStyles.touchArea, touchOverflowStyle]}
            {...this._panResponder.panHandlers}
          >
            {debugTouchArea === true &&
              this._renderDebugThumbTouchRect(thumbLeft)}
          </View>
        </View>
      </View>
    );
  }

  _getPropsForComponentUpdate(props) {
    var {
      value,
      onValueChange,
      onValueBetweenChange,
      onSlidingStart,
      onSlidingComplete,
      onSlidingBetweenComplete,
      style,
      trackStyle,
      thumbStyle,
      trackClickable,
      ...otherProps
    } = props;

    return otherProps;
  }

  _handleStartShouldSetPanResponder = (
    e: Object /*gestureState: Object*/,
  ): boolean => {
    // Should we become active when the user presses down on the thumb?
    return this.props.trackClickable ? true : this._thumbHitTest(e);
  };

  _handleMoveShouldSetPanResponder(/*e: Object, gestureState: Object*/): boolean {
    // Should we become active when the user moves a touch over the thumb?
    return false;
  }

  _handlePanResponderGrant = (e: Object, gestureState: Object) => {
    let currentValue = this._getCurrentValue();
    let thumbLeft = this._getThumbLeft(currentValue);
    this._previousLeft = this.props.trackClickable
      ? e.nativeEvent.locationX - this.state.thumbSize.width / 2 - 5
      : thumbLeft;
    let newValue = this._getValue(gestureState);
    let setEnd = false;
    if (this.props.betweenMode) {
      //if in between - find nearest
      if (
        Math.abs(newValue - currentValue) >
        Math.abs(newValue - this._getEndValue())
      ) {
        currentValue = this._getEndValue();
        thumbLeft = this._getThumbLeft(currentValue);
        this._previousLeft = this.props.trackClickable
          ? e.nativeEvent.locationX - this.state.thumbSize.width / 2 - 5
          : thumbLeft;
        setEnd = true;
      }
    }
    this._fireChangeEvent('onSlidingStart');
    if (this.props.trackClickable) {
      if (this.props.betweenMode) {
        let value = this._getValue(gestureState);
        if (setEnd) {
          this._setEndValue(value);
        } else {
          this._setCurrentValue(value);
        }
        let start = Math.min(
          this.state.value.__getValue(),
          this.state.valueEnd.__getValue(),
        );
        let end = Math.max(
          this.state.value.__getValue(),
          this.state.valueEnd.__getValue(),
        );
        this._fireChangeEvent('onValueBetweenChange', { start, end });
      } else {
        this._setCurrentValue(this._getValue(gestureState));
        this._fireChangeEvent('onValueChange');
      }
    }
  };

  _handlePanResponderMove = (e: Object, gestureState: Object) => {
    if (this.props.disabled) {
      return;
    }

    let newValue = this._getValue(gestureState);
    let setEnd = false;
    if (this.props.betweenMode) {
      //if in between - find nearest
      if (
        Math.abs(newValue - this._getCurrentValue()) >
        Math.abs(newValue - this._getEndValue())
      ) {
        setEnd = true;
      }
    }
    if (this.props.betweenMode) {
      if (setEnd) {
        this._setEndValue(newValue);
      } else {
        this._setCurrentValue(newValue);
      }
      let start = Math.min(
        this.state.value.__getValue(),
        this.state.valueEnd.__getValue(),
      );
      let end = Math.max(
        this.state.value.__getValue(),
        this.state.valueEnd.__getValue(),
      );
      this._fireChangeEvent('onValueBetweenChange', { start, end });
    } else {
      this._setCurrentValue(this._getValue(gestureState));
      this._fireChangeEvent('onValueChange');
    }
  };

  _handlePanResponderRequestEnd(e: Object, gestureState: Object) {
    // Should we allow another component to take over this pan?
    return false;
  }

  _handlePanResponderEnd = (e: Object, gestureState: Object) => {
    if (this.props.disabled) {
      return;
    }

    let newValue = this._getValue(gestureState);
    let setEnd = false;
    if (this.props.betweenMode) {
      //if in between - find nearest
      if (
        Math.abs(newValue - this._getCurrentValue()) >
        Math.abs(newValue - this._getEndValue())
      ) {
        this._setEndValue(this._getValue(gestureState));
      } else {
        this._setCurrentValue(this._getValue(gestureState));
      }

      let start = Math.min(
        this.state.value.__getValue(),
        this.state.valueEnd.__getValue(),
      );
      let end = Math.max(
        this.state.value.__getValue(),
        this.state.valueEnd.__getValue(),
      );
      this._fireChangeEvent('onSlidingBetweenComplete', { start, end });
    } else {
      this._setCurrentValue(this._getValue(gestureState));
      this._fireChangeEvent('onSlidingComplete');
    }
  };

  _measureContainer = (x: Object) => {
    if (this.props.useInitialContainerSize) {
      return;
    }
    this._handleMeasure('containerSize', x);
  };

  _measureTrack = (x: Object) => {
    this._handleMeasure('trackSize', x);
  };

  _measureThumb = (x: Object) => {
    this._handleMeasure('thumbSize', x);
  };

  _handleMeasure = (name: string, x: Object) => {
    var { width, height } = x.nativeEvent.layout;
    var size = { width: width, height: height };

    var storeName = `_${name}`;
    var currentSize = this[storeName];
    if (
      currentSize &&
      width === currentSize.width &&
      height === currentSize.height
    ) {
      return;
    }
    this[storeName] = size;

    if (name === 'containerSize') {
      this.setState({
        containerSize: { width: width, height: 0 },
        trackSize: { width: width - 60, height: 0 },
        thumbSize: { width: 30, height: 0 },
        allMeasured: true,
      });
    }
  };

  _getRatio = (value: number) => {
    return (
      (value - this.props.minimumValue) /
      (this.props.maximumValue - this.props.minimumValue)
    );
  };

  _getThumbLeft = (value: number) => {
    var ratio = this._getRatio(value);
    const result =
      ratio * (this.state.trackSize.width - this.state.thumbSize.width);
    return result;
  };

  _getValue = (gestureState: Object) => {
    var length = this.state.trackSize.width - this.state.thumbSize.width;
    var thumbLeft = this._previousLeft + gestureState.dx;

    var ratio = thumbLeft / length;

    if (this.props.step) {
      return Math.max(
        this.props.minimumValue,
        Math.min(
          this.props.maximumValue,
          this.props.minimumValue +
            Math.round(
              (ratio * (this.props.maximumValue - this.props.minimumValue)) /
                this.props.step,
            ) *
              this.props.step,
        ),
      );
    } else {
      return Math.max(
        this.props.minimumValue,
        Math.min(
          this.props.maximumValue,
          ratio * (this.props.maximumValue - this.props.minimumValue) +
            this.props.minimumValue,
        ),
      );
    }
  };

  _getCurrentValue = () => {
    return this.state.value.__getValue();
  };

  _setCurrentValue = (value: number) => {
    this.state.value.setValue(value);
    if (
      this.props.betweenMode &&
      this.state.valueEnd.__getValue() < this.state.value.__getValue()
    ) {
      this.state.value.setValue(this.state.valueEnd.__getValue());
      this.state.valueEnd.setValue(value);
    }
  };
  _getEndValue = () => {
    return this.state.valueEnd.__getValue();
  };

  _setEndValue = (value: number) => {
    this.state.valueEnd.setValue(value);
    if (this.state.valueEnd.__getValue() < this.state.value.__getValue()) {
      this.state.valueEnd.setValue(this.state.value.__getValue());
      this.state.value.setValue(value);
    }
  };

  _setCurrentValueAnimated = (value: number) => {
    var animationType = this.props.animationType;
    var animationConfig = Object.assign(
      {},
      DEFAULT_ANIMATION_CONFIGS[animationType],
      this.props.animationConfig,
      { toValue: value },
    );

    Animated[animationType](this.state.value, animationConfig).start();
  };

  _fireChangeEvent = (event, value) => {
    if (this.props[event]) {
      this.props[event](value || this._getCurrentValue());
    }
  };

  _getTouchOverflowSize = () => {
    var state = this.state;
    var props = this.props;

    var size = {};
    if (state.allMeasured === true) {
      size.width = Math.max(
        0,
        props.thumbTouchSize.width - state.thumbSize.width,
      );
      size.height = Math.max(
        0,
        props.thumbTouchSize.height - state.containerSize.height,
      );
    }

    return size;
  };

  _getTouchOverflowStyle = () => {
    var { width, height } = this._getTouchOverflowSize();

    var touchOverflowStyle = {};
    if (width !== undefined && height !== undefined) {
      var verticalMargin = -height / 2;
      touchOverflowStyle.marginTop = verticalMargin;
      touchOverflowStyle.marginBottom = verticalMargin;

      var horizontalMargin = -width / 2;
      touchOverflowStyle.marginLeft = horizontalMargin;
      touchOverflowStyle.marginRight = horizontalMargin;
    }

    if (this.props.debugTouchArea === true) {
      touchOverflowStyle.backgroundColor = 'orange';
      touchOverflowStyle.opacity = 0.5;
    }

    return touchOverflowStyle;
  };

  _thumbHitTest = (e: Object) => {
    var nativeEvent = e.nativeEvent;
    var thumbTouchRect = this._getThumbTouchRect();
    return thumbTouchRect.containsPoint(
      nativeEvent.locationX,
      nativeEvent.locationY,
    );
  };

  _getThumbTouchRect = () => {
    var state = this.state;
    var props = this.props;
    var touchOverflowSize = this._getTouchOverflowSize();

    return new Rect(
      touchOverflowSize.width / 2 +
        this._getThumbLeft(this._getCurrentValue()) +
        (state.thumbSize.width - props.thumbTouchSize.width) / 2,
      touchOverflowSize.height / 2 +
        (state.containerSize.height - props.thumbTouchSize.height) / 2,
      props.thumbTouchSize.width,
      props.thumbTouchSize.height,
    );
  };

  _renderDebugThumbTouchRect = (thumbLeft) => {
    var thumbTouchRect = this._getThumbTouchRect();
    var positionStyle = {
      left: thumbLeft,
      top: thumbTouchRect.y,
      width: thumbTouchRect.width,
      height: thumbTouchRect.height,
    };

    return (
      <Animated.View
        style={[defaultStyles.debugThumbTouchArea, positionStyle]}
        pointerEvents="none"
      />
    );
  };

  _renderThumbImage = () => {
    return (
      <View
        style={{
          width: 30,
          height: 30,
          borderRadius: 15,
          backgroundColor: '#626262',
        }}
      >
        <View
          style={{
            position: 'absolute',
            width: 18,
            height: 18,
            left: 6,
            top: 6,
            backgroundColor: '#ffffff',
            borderRadius: 9,
          }}
        />
      </View>
    );
  };
}

var defaultStyles = StyleSheet.create({
  container: {
    height: 40,
    justifyContent: 'center',
  },
  track: {
    height: TRACK_SIZE,
    borderRadius: TRACK_SIZE / 2,
  },
  thumb: {
    position: 'absolute',
    width: THUMB_SIZE,
    height: THUMB_SIZE,
    borderRadius: THUMB_SIZE / 2,
  },
  touchArea: {
    position: 'absolute',
    backgroundColor: 'transparent',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  debugThumbTouchArea: {
    position: 'absolute',
    backgroundColor: 'green',
    opacity: 0.5,
  },
});

export default withTheme(Slider);
